import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Check, Info } from "lucide-react";
import { useWatch } from "react-hook-form";
import { OptionsFieldArray } from "../OptionsFieldArray";
import { ParameterRowProps } from ".";
import { FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Checkbox } from "@/components/ui/checkbox";



export function ParameterRow({
    index,
    parameterName,
    deleteParameter,
    form
}: Readonly<ParameterRowProps>) {
    const options = useWatch({ control: form.control, name: `parameter.${parameterName}.${index}.options` });
    const [showValueSection, setShowValueSection] = useState(false);

    const showValueSectionHandler = () => {
        setShowValueSection(true);
    }
    const hideValueSectionHandler = () => {
        form.setValue(`parameter.${parameterName}.${index}.options`, undefined);
        setShowValueSection(false);
    }

    useEffect(() => {
        if (Array.isArray(options)) {
            setShowValueSection(true);
        } else {
            setShowValueSection(false);
        }
    }, [options])

    return (
        <div className="flex-col items-center p-3 space-y-3 border rounded-lg">
            <div className="flex-1 space-y-2">
                <FormField
                    name={`parameter.${parameterName}.${index}.name`}
                    control={form.control}
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel className="text-sm font-medium leading-none">Name</FormLabel>
                            <div className="flex items-center gap-2">
                                <Input {...field} />
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button
                                                variant="destructive"
                                                size='action'
                                                className={`text-nowrap flex text-sm`}
                                                onClick={() => deleteParameter(index)}
                                            >
                                                <i className="text-md bi bi-trash"></i>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Delete</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>

                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            <div className="flex gap-4">
                <FormField
                    name={`parameter.${parameterName}.${index}.settings.isPrivate`}
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                            <Checkbox 
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <FormLabel className="font-normal">
                                            Is private?
                                        </FormLabel>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Does parameter store sensitive data like API key and should be hidden?</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    name={`parameter.${parameterName}.${index}.settings.isRequiredForExport`}
                    control={form.control}
                    render={({ field }) => (
                        <FormItem className="flex items-center gap-2 space-y-0">
                            <Checkbox 
                                checked={field.value}
                                onCheckedChange={field.onChange}
                            />
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <FormLabel className="font-normal">
                                            Is required for export?
                                        </FormLabel>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Is parameter required for export to work correctly? / Should parameter always exported?</p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>

                            <FormMessage />
                        </FormItem>
                    )}
                />
            </div>
            {['crm', 'project', 'survey', 'question'].includes(parameterName) && (
                <div className="flex flex-col w-full gap-1 mt-2">
                    <div className="flex items-center mb-2 text-blue-600">
                        <Info className="flex-shrink-0 me-1" size="16" />
                        <p className="text-xs">If the values are NOT constant (or specific to each {parameterName}), you can enter it in the next step</p>
                    </div>

                    {!showValueSection && (
                        <div className="flex gap-2 w-100">
                            <div className="flex items-center gap-2">
                                <Button type="button" size="action" variant="outline" className="flex flex-col items-center h-auto" onClick={showValueSectionHandler}></Button>
                                <p className="text-xs font-normal text-blue-600">Select this box if all {parameterName}s share the same/fixed data element value or set of values.</p>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {showValueSection && (
                <div className="flex flex-col gap-2 p-2 border rounded-lg">
                    {['crm', 'project', 'survey', 'question'].includes(parameterName) && (
                        <div className="flex items-center gap-2">
                            <Button type="button" size="action" variant="outline" className="flex flex-col items-center h-auto" onClick={hideValueSectionHandler}>
                                <Check size="18" />
                            </Button>
                            <p className="text-xs font-normal text-blue-600">Select this box if all {parameterName}s share the same/fixed data element value or set of values.</p>
                        </div>
                    )}

                    <OptionsFieldArray
                        index={index}
                        form={form}
                        parameterName={parameterName}
                    />
                </div>
            )}

        </div>
    );
}
